/**
 * Error Fallback Component
 *
 * Displays when an unhandled error is caught by Sentry or PostHog Error Boundary.
 * Provides user feedback options and recovery actions.
 *
 * Supports both:
 * - Sentry ErrorBoundary props: {error, componentStack, eventId, resetError}
 * - PostHog ErrorBoundary props: {error, exceptionEvent, componentStack}
 */

import {useEffect} from 'react';
import * as Sentry from '@sentry/react';
import {usePostHog} from '@posthog/react';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {BookX, Home, RefreshCw, MessageSquare} from "lucide-react";
import {isDebug} from "@/lib/debug";
import {instrumentationProvider} from '../instrumentation'

// [SEL-6] Error boundary fallback UI with Sentry user feedback dialog
// Sentry props - from Sentry.ErrorBoundary
interface SentryErrorFallbackProps {
  error: unknown;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}

// PostHog props - from PostHogErrorBoundary
interface PostHogErrorFallbackProps {
  error: unknown;
  exceptionEvent: unknown;
  componentStack: string;
}

// Union type to support both
type ErrorFallbackProps = SentryErrorFallbackProps | PostHogErrorFallbackProps;

export const ErrorFallback = (props: ErrorFallbackProps) => {
  const posthog = usePostHog();

  // Type guard to check if it's Sentry props
  const isSentryProps = (p: ErrorFallbackProps): p is SentryErrorFallbackProps => {
    return 'eventId' in p && 'resetError' in p;
  };

  const error = props.error;
  const componentStack = props.componentStack;
  const eventId = isSentryProps(props) ? props.eventId : null;
  const exceptionEvent = !isSentryProps(props) ? props.exceptionEvent : null;

  useEffect(() => {
    // Capture exception in PostHog if not using Sentry
    // NOTE: Sentry.ErrorBoundary automatically calls Sentry.captureException() internally,
    // but PostHogErrorBoundary does NOT auto-capture - we must manually call
    // posthog.captureException() to send the error event to PostHog.
    // The capture_exceptions option in PostHogProvider only applies to window.onerror/
    // window.onunhandledrejection, not React error boundaries.
    if (!eventId && instrumentationProvider === "posthog" && posthog) {
      posthog.captureException(error, {
        componentStack,
      });
      if (isDebug()) {
        console.log('[ErrorFallback] Exception captured in PostHog');
      }
    }

    // Log error details in development
    if (isDebug()) {
      console.error('[ErrorFallback] Caught error:', {
        error,
        componentStack,
        eventId,
        exceptionEvent,
        provider: eventId ? 'Sentry' : 'PostHog',
      });
    }
  }, [error, componentStack, eventId, exceptionEvent, posthog]);

  const handleFeedback = async () => {
    // Only Sentry supports feedback widget currently
    if (!eventId) {
      console.warn('[ErrorFallback] Feedback only available for Sentry errors');
      return;
    }

    // Open Sentry feedback form (v8 API)
    try {
      const feedback = Sentry.getFeedback();
      if (!feedback) {
        console.warn('[ErrorFallback] Sentry feedback integration not available');
        return;
      }

      const form = await feedback.createForm({
        messagePlaceholder: 'What happened? What were you doing when the error occurred?',
        tags: {
          'feedback.source': 'error-boundary',
          eventId: eventId || 'unknown',
        },
      });

      form.appendToDom();
      form.open();
    } catch (error) {
      console.error('[ErrorFallback] Failed to open Sentry feedback form:', error);
    }
  };

  const handleReload = () => {
    window.location.href = '/';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Handle both Error objects and unknown error types
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-6">
      <Card className="max-w-md w-full shadow-lg">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-primary/10 p-6">
              <BookX className="h-16 w-16 text-primary" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold mb-2">Wizard Malfunction</CardTitle>
          <CardDescription className="text-lg">
            The wizard encountered a technical hiccup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-muted-foreground">
            Don't worry—your book project is safe. This is just a temporary site issue.
            Try refreshing the page, heading back home, or send us feedback about what happened.
          </p>

          {isDebug() && (
            <div className="mt-4 p-3 bg-muted rounded-md space-y-2">
              <p className="text-xs font-semibold">Error Details (Development Only):</p>
              <p className="text-xs font-mono text-destructive break-words">
                {errorMessage}
              </p>
              {eventId && (
                <p className="text-xs text-muted-foreground">
                  Sentry Event ID: {eventId}
                </p>
              )}
              {componentStack && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">
                    Component Stack
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap text-xs">{componentStack}</pre>
                </details>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="flex-1"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Page
            </Button>
            <Button
              onClick={handleReload}
              className="flex-1"
            >
              <Home className="mr-2 h-4 w-4" />
              Go Home
            </Button>
          </div>
          {eventId && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={handleFeedback}
                variant="secondary"
                size="sm"
                className="w-full sm:w-auto"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Send Feedback
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
